import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Form, Input, Select} from 'antd';
import Accordion from '../../../../Accordion';
import AntdCountriesSelect from '../../../../Select/AntdCountriesSelect';
import CompanyTagSelect from '../../../TransactionsPage/CompanyTagSelect/CompanyTagSelect';
import {
  kycHelpers,
  VendorPriorityOptions,
} from '../../../../../utils/helpers';
import {tagFamilyConstants} from '../../../../../constants';
import {validation} from '../../../../../utils/validation';
import {useIsTreezorEnabled} from '../../../../../hooks';

const {Item} = Form;

const formFieldProps = {
  size: 'large'
}

const countrySelectProps = {
  ...formFieldProps,
  ...kycHelpers.getCountrySelectProps()
}

const FullVendorFormItems = ({
  initialFormValues = {}
}) => {
  const [t] = useTranslation(['main', 'vendors']);
  const [priorityOptions, ] = useState(VendorPriorityOptions());

  const isTreezorEnabled = useIsTreezorEnabled();

  const validationRules = useMemo(() => ({
    bic: validation.bic(t, false),
    iban: validation.iban(t, false),
    email: validation.email(t, false),
    phone: validation.phone(t, false),
    required: validation.required(t),
    vat: validation.vat(t),
  }), [t]);

  const requiredItemProps = useMemo(() => ({rules: validationRules.required, required: true}), [validationRules.required]);

  const trans = (key) => t(`vendors:${key}`);

  const formT = (key) => trans(`modal.addVendor.${key}`);

  const placeholder = (key) => formT(`placeholder.${key}`);

  return (
    <>
      <Item
        {...requiredItemProps}
        label={t('name')}
        name='name'
      >
        <Input
          {...formFieldProps}
          placeholder={placeholder('name')}
        />
      </Item>
      <Item
        label={t('country')}
        name='country'
      >
        <AntdCountriesSelect
          {...countrySelectProps}
          countryPropName='name'
          placeholder={placeholder('country')}
        />
      </Item>
      <Item
        {...requiredItemProps}
        label={t('priority')}
        name='priority'
      >
        <Select
          {...formFieldProps}
          placeholder={placeholder('priority')}
          options={priorityOptions}
        />
      </Item>
      <Item
        label={t('category')}
        name='category'
      >
        <CompanyTagSelect
          inputProps={{...formFieldProps, placeholder: placeholder('category')}}
          isEnableAdd={false}
          selected={initialFormValues.category}
          tagFamily={tagFamilyConstants.VENDORS}
        />
      </Item>
      <Item
        label={t('vatNumber')}
        name='vat'
        rules={validationRules.vat}
        required
      >
        <Input
          {...formFieldProps}
          maxLength={15}
          placeholder={placeholder('vat')}
        />
      </Item>
      <Accordion
        items={[
          {
            key: '1',
            label: `${t('contact')} ${t('details')}`,
            forceRender: true,
            children: (
              <>
                <Item
                  label={t('address')}
                  name='address'
                >
                  <Input
                    {...formFieldProps}
                    placeholder={placeholder('address')}
                  />
                </Item>
                <Item
                  label={t('city')}
                  name='city'
                >
                  <Input
                    {...formFieldProps}
                    placeholder={placeholder('city')}
                  />
                </Item>
                <Item
                  label={t('contactPerson')}
                  name='contract_person'
                >
                  <Input
                    {...formFieldProps}
                    placeholder={placeholder('contactPerson')}
                  />
                </Item>
                <Item
                  label={t('phoneNumber')}
                  name='phone_number'
                  rules={validationRules.phone}
                >
                  <Input
                    {...formFieldProps}
                    placeholder={placeholder('phoneNumber')}
                  />
                </Item>
                <Item
                  label={t('emailAddress')}
                  name='email'
                  rules={validationRules.email}
                >
                  <Input
                    {...formFieldProps}
                    placeholder={placeholder('emailAddress')}
                  />
                </Item>
              </>
            )
          },
          ...(isTreezorEnabled ? [{
            key: '2',
            label: `${t('bank')} ${t('details')}`,
            forceRender: true,
            children: (
              <>
                <Item
                  label={t('iban')}
                  name='iban'
                  rules={validationRules.iban}
                >
                  <Input
                    {...formFieldProps}
                    placeholder={placeholder('iban')}
                  />
                </Item>
                <Item
                  label={t('bic/swift')}
                  name='bic'
                  rules={validationRules.bic}
                >
                  <Input
                    {...formFieldProps}
                    placeholder={placeholder('bic')}
                  />
                </Item>
              </>
            )
          }
        ] : [])
        ]}
      />
    </>
  );
}

FullVendorFormItems.propTypes = {
  initialFormValues: PropTypes.shape({
    category: PropTypes.any
  })
}

export default FullVendorFormItems;
